import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Media } from "@/types/media";

interface HeroCarouselProps {
  items: Media[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  const go = useCallback(
    (next: number) => {
      setIndex((next + items.length) % items.length);
    },
    [items.length]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  return (
    <section className="relative h-[440px] w-full overflow-hidden md:h-[600px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === index ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} 在线观看`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/70 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] items-center px-4 md:px-6">
        <div key={items[index].id} className="max-w-xl animate-fade-up">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
              {items[index].kind === "movie" ? "热门电影" : "热播剧集"}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-background-100/80 px-3 py-1 text-xs font-medium text-accent-400 backdrop-blur">
              <i className="ri-star-fill text-[11px]"></i>
              {items[index].rating.toFixed(1)}
            </span>
          </div>

          <h2 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-5xl">
            {items[index].title}
          </h2>
          <p className="mt-2 text-sm text-foreground-500 md:text-base">
            {items[index].genres.join(" · ")} · {items[index].region} · {items[index].year}
          </p>
          <p className="mt-4 line-clamp-2 max-w-lg text-sm leading-relaxed text-foreground-600 md:line-clamp-3 md:text-base">
            {items[index].synopsis}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${items[index].id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-play-circle-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/detail/${items[index].id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/60 bg-background-100/60 px-5 py-2.5 text-sm font-medium text-foreground-800 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-500"
            >
              <i className="ri-information-line text-lg"></i>
              影片详情
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 个推荐`}
            onClick={() => go(i)}
            className={`h-2 rounded-full transition-all duration-300 ${
              i === index ? "w-8 bg-primary-500" : "w-2 bg-background-400/60 hover:bg-background-300"
            }`}
          ></button>
        ))}
      </div>

      <button
        type="button"
        aria-label="上一个推荐"
        onClick={() => go(index - 1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-300/50 bg-background-100/60 text-foreground-800 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-500 md:left-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一个推荐"
        onClick={() => go(index + 1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-background-300/50 bg-background-100/60 text-foreground-800 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-500 md:right-6 md:h-11 md:w-11"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}